import { useState, useEffect } from 'react';
import axios from 'axios';
import './CloudPages.css';
import Menu from '../Shared/Menu';
import usePageTitle from '../Shared/usePageTitle';

const API_URL    = '/rest/api/v1/assets';
const DETAIL_URL = '/rest/api/v1/assets'; // detail: GET {DETAIL_URL}/{assetId}

const TYPES = ['All types', 'Landing Page', 'Preference Centre', 'Survey Page', 'Webpage'];

/* ─── Helpers ─── */
function formatNumber(num) {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return String(num);
}

function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function mapAssetType(item) {
    const id   = item.assetTypeId;
    const name = (item.assetTypeName || item.assetType?.name || item.contentType || '').toLowerCase();
    // SFMC asset type IDs: 205=Landing Page, 206=Preference Page, 207=Survey, 208=Microsites
    if (id === 205 || name.includes('landing') || name.includes('webpage')) return 'Landing Page';
    if (id === 206 || name.includes('pref')) return 'Preference Centre';
    if (id === 207 || name.includes('survey')) return 'Survey Page';
    if (name.includes('template') || name.includes('html')) return 'Template';
    if (name.includes('email') || id === 208) return 'Email';
    // For image/file assets, use the raw name
    if (name) return name.charAt(0).toUpperCase() + name.slice(1);
    return 'Asset';
}

function normalizeAsset(item) {
    const statusRaw = String(item.status || '').toLowerCase();
    const typeName  = mapAssetType(item);
    return {
        ...item,
        id:          item.id || item.sfmcAssetId || item.assetId,
        sfmcAssetId: item.sfmcAssetId || '',
        customerKey: item.customerKey || '',
        name:        item.name || item.assetName || 'Unnamed Page',
        status:      statusRaw.includes('active') || statusRaw.includes('publish') ? 'Published' : 'Paused',
        type:        typeName,
        assetTypeId: item.assetTypeId || '',
        journey:     item.category?.name || item.journey || item.folderName || '',
        folder:      item.category?.parentName
                        ? `${item.category.parentName}/${item.category.name}`
                        : (item.folder || item.folderPath || ''),
        modified:    formatDate(item.modifiedDate || item.updatedAt || item.createdAt),
        url:         item.views?.html?.url || item.url || item.pageUrl || '',
        views:       item.views?.total || item.pageViews || item.totalViews || 0,
        unique:      item.views?.unique || item.uniqueVisitors || 0,
        conversion:  item.conversionRate || item.conversion || 0,
        bounce:      item.bounceRate || item.bounce || 0,
        avgTime:     item.avgTimeOnPage || item.avgTime || '—',
        blocks:      item.contentBlockCount || item.blocks || item.slots?.length || 0,
        dynamic:     item.dynamicBlockCount || item.dynamic || 0,
        scripts:     item.scriptCount || item.scripts || 0,
        ssl:         item.ssl != null ? item.ssl : true,
        description: item.description || '',
        ownerName:   item.ownerName || '',
        ownerEmail:  item.ownerEmail || '',
    };
}

/* ─── Cloud Page Detail Panel (slideout) ─── */
function CPDetailPanel({ page, onClose }) {
    const [activeTab, setActiveTab] = useState('overview');
    const [detail, setDetail]       = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    useEffect(() => {
        if (!page || !page.id) return;
        setLoadingDetail(true);
        axios.get(`${DETAIL_URL}/${page.id}`)
            .then(res => setDetail(res.data))
            .catch(() => {})
            .finally(() => setLoadingDetail(false));
    }, [page]);

    if (!page) return null;
    const p = detail || page;

    const isPublished = page.status === 'Published';
    const statusCls   = isPublished ? 'cp-badge-status-published' : 'cp-badge-status-paused';
    const dotCls      = isPublished ? 'cp-dot-published' : 'cp-dot-paused';
    const typeCls     = page.type === 'Landing Page' ? 'cp-badge-type-landing'
        : page.type === 'Preference Centre' ? 'cp-badge-type-pref'
        : 'cp-badge-type-survey';

    return (
        <>
            <div className="step-panel-backdrop show" onClick={onClose} />
            <div className="step-panel show">
                {/* Header */}
                <div className="step-panel-header">
                    <div className="d-flex align-items-start justify-content-between w-100 mb-3">
                        <div className="d-flex align-items-center gap-3">
                            <div className="step-icon modal-step-icon" style={{ background: '#F8FAFC', border: '1.5px solid #E2E8F0', color: '#64748B', fontSize: 20 }}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                                    <rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" />
                                </svg>
                            </div>
                            <div>
                                <h2 className="step-panel-title">{page.name}</h2>
                                <p style={{ fontSize: 12, color: '#94A3B8', margin: 0 }}>{page.folder}</p>
                            </div>
                        </div>
                        <button className="step-panel-close" onClick={onClose}>✕</button>
                    </div>

                    {/* Badges */}
                    <div className="d-flex gap-2 flex-wrap mb-3">
                        <span className={`cp-badge ${statusCls}`}>
                            <span className={`cp-badge-dot ${dotCls}`} />
                            {page.status}
                        </span>
                        <span className={`cp-badge ${typeCls}`}>{page.type}</span>
                        <span className="cp-badge cp-badge-journey">{page.journey}</span>
                    </div>

                    {/* Quick Stats */}
                    <div className="modal-quick-stats">
                        <div className="modal-stat-box">
                            <p className="modal-stat-label">Views</p>
                            <p className="modal-stat-value" style={{ color: '#6366F1' }}>{formatNumber(page.views)}</p>
                        </div>
                        <div className="modal-stat-box">
                            <p className="modal-stat-label">Conversion</p>
                            <p className="modal-stat-value" style={{ color: '#10B981' }}>{page.conversion}%</p>
                        </div>
                        <div className="modal-stat-box">
                            <p className="modal-stat-label">Bounce Rate</p>
                            <p className="modal-stat-value" style={{ color: '#EF4444' }}>{page.bounce}%</p>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="step-panel-tabs d-flex">
                    {[
                        { key: 'overview', label: 'Overview' },
                        { key: 'analytics', label: 'Analytics' },
                        { key: 'blocks', label: `Content Blocks (${page.blocks})` },
                        { key: 'scripts', label: `Scripts (${page.scripts})` },
                    ].map(t => (
                        <button key={t.key} className={`modal-tab${activeTab === t.key ? ' active' : ''}`} onClick={() => setActiveTab(t.key)}>{t.label}</button>
                    ))}
                </div>

                {/* Body */}
                <div className="step-panel-body">
                    {activeTab === 'overview' && (
                        <>
                            <div className="detail-section">
                                <p className="detail-section-title">Description</p>
                                <div className="detail-section-content">
                                    {page.description || `${page.type} for the ${page.journey} journey.`}
                                </div>
                            </div>

                            <div className="detail-section">
                                <p className="detail-section-title">Page URL</p>
                                <div style={{
                                    background: '#0F172A', borderRadius: 12, padding: '12px 16px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12
                                }}>
                                    <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#86EFAC', wordBreak: 'break-all' }}>{page.url}</span>
                                    {page.ssl && <span style={{ fontSize: 11, fontWeight: 700, color: '#10B981', background: '#ECFDF5', border: '1px solid #A7F3D0', padding: '2px 8px', borderRadius: 6, flexShrink: 0 }}>SSL</span>}
                                </div>
                            </div>

                            <div className="detail-section">
                                <p className="detail-section-title">Details</p>
                                <div className="detail-table">
                                    <div className="detail-table-row">
                                        <span className="detail-table-label">Journey</span>
                                        <span className="detail-table-value">{page.journey}</span>
                                    </div>
                                    <div className="detail-table-row">
                                        <span className="detail-table-label">Collection</span>
                                        <span className="detail-table-value">{page.journey.split(' ').pop()}</span>
                                    </div>
                                    <div className="detail-table-row">
                                        <span className="detail-table-label">Folder</span>
                                        <span className="detail-table-value">{page.folder}</span>
                                    </div>
                                    <div className="detail-table-row">
                                        <span className="detail-table-label">Template</span>
                                        <span className="detail-table-value">{page.type}</span>
                                    </div>
                                    <div className="detail-table-row">
                                        <span className="detail-table-label">AMPscript</span>
                                        <span className="detail-table-value">Enabled</span>
                                    </div>
                                    <div className="detail-table-row">
                                        <span className="detail-table-label">Modified</span>
                                        <span className="detail-table-value">{page.modified}</span>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {activeTab === 'analytics' && (
                        <div className="detail-section">
                            <p className="detail-section-title">Performance Metrics</p>
                            <div className="detail-table">
                                <div className="detail-table-row"><span className="detail-table-label">Total Views</span><span className="detail-table-value">{formatNumber(page.views)}</span></div>
                                <div className="detail-table-row"><span className="detail-table-label">Unique Visitors</span><span className="detail-table-value">{formatNumber(page.unique)}</span></div>
                                <div className="detail-table-row"><span className="detail-table-label">Conversion Rate</span><span className="detail-table-value" style={{ color: '#10B981' }}>{page.conversion}%</span></div>
                                <div className="detail-table-row"><span className="detail-table-label">Bounce Rate</span><span className="detail-table-value" style={{ color: '#EF4444' }}>{page.bounce}%</span></div>
                                <div className="detail-table-row"><span className="detail-table-label">Avg Time on Page</span><span className="detail-table-value">{page.avgTime}</span></div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'blocks' && (
                        <div className="detail-section">
                            <p className="detail-section-title">Content Blocks</p>
                            <p style={{ fontSize: 13, color: '#64748B' }}>{page.blocks} content blocks, {page.dynamic} dynamic</p>
                        </div>
                    )}

                    {activeTab === 'scripts' && (
                        <div className="detail-section">
                            <p className="detail-section-title">Scripts</p>
                            <p style={{ fontSize: 13, color: '#64748B' }}>{page.scripts} scripts (AMPscript, SSJS, JS)</p>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

/* ─── Cloud Page Row Card ─── */
function CPRowCard({ page, onClick }) {
    const isPublished = page.status === 'Published';
    const accentClass = isPublished ? 'cp-accent-published' : 'cp-accent-paused';
    const statusCls   = isPublished ? 'cp-badge-status-published' : 'cp-badge-status-paused';
    const dotCls      = isPublished ? 'cp-dot-published' : 'cp-dot-paused';

    const t = (page.type || '').toLowerCase();
    const typeCls = t.includes('landing') || t.includes('webpage') ? 'cp-badge-type-landing'
        : t.includes('pref') ? 'cp-badge-type-pref'
        : t.includes('survey') ? 'cp-badge-type-survey'
        : 'cp-badge-type-landing';

    return (
        <div className="cp-row-card" onClick={onClick} style={{ cursor: 'pointer' }}>
            <div className={`cp-accent-stripe ${accentClass}`} />
            <div className="cp-card-content">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                            <h3 className="cp-title">{page.name}</h3>
                            <span className={`cp-badge cp-badge-status ${statusCls}`}>
                                <span className={`cp-badge-dot ${dotCls}`} />
                                {page.status}
                            </span>
                            <span className={`cp-badge cp-badge-type ${typeCls}`}>{page.type}</span>
                        </div>
                        <p className="cp-meta mb-2">{page.journey} &middot; {page.folder} &middot; Modified {page.modified}</p>
                        <p className="cp-url mb-3">{page.url}</p>
                    </div>
                    <svg className="cp-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2.5">
                        <path d="M9 18l6-6-6-6" />
                    </svg>
                </div>

                {/* Metrics + Pills */}
                <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                    <div className="cp-metrics">
                        <div className="cp-metric">
                            <p className="cp-metric-label">Views</p>
                            <p className="cp-metric-value cp-metric-primary">{formatNumber(page.views)}</p>
                        </div>
                        <div className="cp-metric">
                            <p className="cp-metric-label">Unique</p>
                            <p className="cp-metric-value cp-metric-info">{formatNumber(page.unique)}</p>
                        </div>
                        <div className="cp-metric">
                            <p className="cp-metric-label">Conversion</p>
                            <p className="cp-metric-value cp-metric-success">{page.conversion}%</p>
                        </div>
                        <div className="cp-metric">
                            <p className="cp-metric-label">Bounce</p>
                            <p className="cp-metric-value cp-metric-warn">{page.bounce}%</p>
                        </div>
                        <div className="cp-metric">
                            <p className="cp-metric-label">Avg Time</p>
                            <p className="cp-metric-value cp-metric-default">{page.avgTime}</p>
                        </div>
                    </div>

                    <div className="cp-pills">
                        <span className="email-blocks-badge">{page.blocks} blocks</span>
                        <span className="email-dynamic-badge">{page.dynamic} dynamic</span>
                        <span className="cp-pill cp-pill-scripts">{page.scripts} scripts</span>
                        {page.ssl && <span className="cp-pill cp-pill-ssl">SSL</span>}
                    </div>
                </div>

                {/* Progress bar */}
                <div className="progress-bar-custom mt-3">
                    <div className="progress-bar-fill" style={{ width: `${page.conversion}%`, background: '#10B981' }} />
                </div>
            </div>
        </div>
    );
}

/* ─── Main Page ─── */
function CloudPages() {
    usePageTitle('Cloud Pages');
    const [pages,         setPages]         = useState([]);
    const [loading,       setLoading]       = useState(true);
    const [error,         setError]         = useState(null);
    const [searchQuery,   setSearchQuery]   = useState('');
    const [statusFilter,  setStatusFilter]  = useState('All');
    const [typeFilter,    setTypeFilter]    = useState('All types');
    const [journeyFilter, setJourneyFilter] = useState('All Journeys');
    const [selectedPage,  setSelectedPage]  = useState(null);

    useEffect(() => {
        axios.get(API_URL)
            .then(res => {
                const data = res.data;
                const list = Array.isArray(data) ? data : (data.items || data.assets || data.data || []);
                setPages(list.map(normalizeAsset));
            })
            .catch(err => setError(err.response?.data?.message || err.message))
            .finally(() => setLoading(false));
    }, []);

    const filtered = pages.filter(p => {
        if (statusFilter !== 'All' && p.status !== statusFilter) return false;
        if (typeFilter !== 'All types' && p.type !== typeFilter) return false;
        if (journeyFilter !== 'All Journeys' && p.journey !== journeyFilter) return false;
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            return (p.name && p.name.toLowerCase().includes(q))
                || (p.url && p.url.toLowerCase().includes(q))
                || (p.journey && p.journey.toLowerCase().includes(q));
        }
        return true;
    });

    // Extract unique journeys from data
    const journeySet = new Set(pages.map(p => p.journey).filter(Boolean));
    const JOURNEYS   = ['All Journeys', ...Array.from(journeySet)];

    // KPIs
    const totalPages     = pages.length;
    const publishedCount = pages.filter(p => p.status === 'Published').length;
    const totalViews     = pages.reduce((s, p) => s + (p.views || 0), 0);
    const totalUnique    = pages.reduce((s, p) => s + (p.unique || 0), 0);
    const avgConversion  = totalPages > 0 ? (pages.reduce((s, p) => s + (p.conversion || 0), 0) / totalPages).toFixed(1) : '0';
    const totalScripts   = pages.reduce((s, p) => s + (p.scripts || 0), 0);

    return (
        <>
            <Menu />

            <div className="container fade-up container-main">
                <div className="page-header mb-4">
                    <h1>Cloud Pages</h1>
                    <p>All landing pages, preference centres and survey pages — with content blocks, scripts, and analytics</p>
                </div>

                {loading && <div className="text-center py-5"><p>Loading cloud pages...</p></div>}
                {error && <div className="text-center py-5"><p className="text-danger">Failed to load: {error}</p></div>}

                {!loading && !error && <>
                {/* KPI Tiles */}
                <div className="row g-3 mb-4">
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon kpi-icon-cp-pages">⬡</div>
                            <div>
                                <p className="kpi-label">Total Pages</p>
                                <p className="kpi-value">{totalPages}</p>
                                <p className="kpi-subtext">{publishedCount} published</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon kpi-icon-cp-views">◈</div>
                            <div>
                                <p className="kpi-label">Total Views</p>
                                <p className="kpi-value">{formatNumber(totalViews)}</p>
                                <p className="kpi-subtext">{formatNumber(totalUnique)} unique visitors</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon kpi-icon-cp-conv">▶</div>
                            <div>
                                <p className="kpi-label">Avg Conversion</p>
                                <p className="kpi-value">{avgConversion}%</p>
                                <p className="kpi-subtext">primary CTA clicks</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="kpi-card d-flex align-items-start gap-3">
                            <div className="kpi-icon kpi-icon-cp-scripts">⌥</div>
                            <div>
                                <p className="kpi-label">Unique Scripts</p>
                                <p className="kpi-value">{totalScripts}</p>
                                <p className="kpi-subtext">AMPscript, SSJS &amp; JS</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="cp-filter-block mb-3">
                    <div className="cp-filter-row">
                        <div className="search-wrapper">
                            <svg className="search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2">
                                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                            </svg>
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Search by name, URL or journey..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <div className="filter-buttons cp-filter-buttons">
                            {['All', 'Published', 'Paused'].map(s => (
                                <button
                                    key={s}
                                    className={`filter-btn${statusFilter === s ? ' active' : ''}`}
                                    onClick={() => setStatusFilter(s)}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                        <div className="cp-type-select-wrapper">
                            <select
                                className="form-select cp-type-select"
                                value={typeFilter}
                                onChange={e => setTypeFilter(e.target.value)}
                            >
                                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Journey chips */}
                    <div className="cp-journey-chips">
                        {JOURNEYS.map(j => (
                            <button
                                key={j}
                                className={`cp-chip${journeyFilter === j ? ' active' : ''}`}
                                onClick={() => setJourneyFilter(j)}
                            >
                                {j}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Results count */}
                <p className="cp-results-count mb-3">
                    Showing <strong>{filtered.length}</strong> of {pages.length} cloud pages
                </p>

                {/* Page Cards */}
                {filtered.length > 0 ? (
                    <div className="d-flex flex-column gap-3">
                        {filtered.map(p => (
                            <CPRowCard key={p.id} page={p} onClick={() => setSelectedPage(p)} />
                        ))}
                    </div>
                ) : (
                    <div className="empty-state">
                        <div className="empty-state-icon">📭</div>
                        <h3 className="empty-state-title">No cloud pages found</h3>
                        <p className="empty-state-text">No cloud pages match your current filters.</p>
                    </div>
                )}
                </>}
            </div>

            {/* Detail Slideout Panel */}
            {selectedPage && (
                <CPDetailPanel page={selectedPage} onClose={() => setSelectedPage(null)} />
            )}
        </>
    );
}

export default CloudPages;
